import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ApiDate, ApiTime } from "../lib/types";
import { MonthGrid } from "./MonthGrid";

export interface DateTimeValue {
  start: ApiDate;
  end: ApiDate;
  // `null` is an all-day event.
  time: { start: ApiTime; end: ApiTime } | null;
}

interface DateTimeFieldProps {
  value: DateTimeValue;
  onChange: (next: DateTimeValue) => void;
}

function isSameDate(a: ApiDate, b: ApiDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

function cmpDate(a: ApiDate, b: ApiDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

function jsToApi(d: Date): ApiDate {
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

function todayApi(): ApiDate {
  return jsToApi(new Date());
}

function addDaysApi(d: ApiDate, n: number): ApiDate {
  const x = new Date(d.year, d.month - 1, d.day);
  x.setDate(x.getDate() + n);
  return jsToApi(x);
}

function nextWeekdayApi(weekday: number): ApiDate {
  // 1 = Monday, 7 = Sunday — match `getDay()`'s 0-6 with Sunday=0 offset.
  const target = weekday % 7;
  const now = new Date();
  let delta = (target - now.getDay() + 7) % 7;
  if (delta === 0) delta = 7;
  now.setDate(now.getDate() + delta);
  return jsToApi(now);
}

function formatApiTime(t: ApiTime): string {
  return `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;
}

function parseTimeInput(value: string): ApiTime | null {
  const m = value.match(/^(\d{2}):(\d{2})$/);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { hour, minute };
}

function formatTriggerLabel(value: DateTimeValue): string {
  const sd = new Date(value.start.year, value.start.month - 1, value.start.day);
  const ed = new Date(value.end.year, value.end.month - 1, value.end.day);
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const multiDay = !isSameDate(value.start, value.end);
  const startDateStr = dateFmt.format(sd);
  const endDateStr = dateFmt.format(ed);

  if (!value.time) {
    return multiDay
      ? `${startDateStr} – ${endDateStr} · all day`
      : `${startDateStr} · all day`;
  }

  const timeFmt = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const startTime = timeFmt.format(
    new Date(0, 0, 0, value.time.start.hour, value.time.start.minute),
  );
  const endTime = timeFmt.format(
    new Date(0, 0, 0, value.time.end.hour, value.time.end.minute),
  );
  if (multiDay) {
    return `${startDateStr}, ${startTime} – ${endDateStr}, ${endTime}`;
  }
  return `${startDateStr} · ${startTime} – ${endTime}`;
}

export function DateTimeField({ value, onChange }: DateTimeFieldProps) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<ApiDate>(() => ({
    year: value.start.year,
    month: value.start.month,
    day: 1,
  }));
  const [multiDay, setMultiDay] = useState(() => !isSameDate(value.start, value.end));
  // Tracks the most recent "anchor" click while in multi-day mode so the
  // next click extends/replaces the range without needing a modifier key.
  const [rangeAnchor, setRangeAnchor] = useState<ApiDate | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPos, setPopoverPos] = useState<
    { top: number; left: number; width: number } | null
  >(null);

  // Snap view to the selection's start whenever the popover opens
  // so we always show the relevant month first.
  useEffect(() => {
    if (open) {
      setViewMonth({ year: value.start.year, month: value.start.month, day: 1 });
      setRangeAnchor(null);
    }
  }, [open, value.start.year, value.start.month]);

  // Pin the portaled popover to the trigger; flip above if there's
  // no room below, and reposition on scroll/resize.
  useEffect(() => {
    if (!open) {
      setPopoverPos(null);
      return;
    }
    const place = () => {
      const t = triggerRef.current;
      if (!t) return;
      const rect = t.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight ?? 380;
      const margin = 6;
      const fitsBelow = rect.bottom + margin + popoverHeight < window.innerHeight;
      const top = fitsBelow
        ? rect.bottom + margin
        : Math.max(8, rect.top - margin - popoverHeight);
      // Cap width to the viewport (minus 16px breathing room) so a wide
      // trigger near a narrow viewport edge can't push the popover off
      // screen, then clamp the left so it stays fully visible.
      const maxWidth = Math.max(220, window.innerWidth - 16);
      const width = Math.min(rect.width, maxWidth);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      setPopoverPos({ top, left, width });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  // Outside-click (excluding both the trigger and the portaled popover)
  // and Escape dismiss the popover.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSelectDate = useCallback(
    (clicked: ApiDate) => {
      if (!multiDay) {
        onChange({ ...value, start: clicked, end: clicked });
        return;
      }
      // Multi-day mode: first click resets anchor; second click closes range.
      if (rangeAnchor == null) {
        onChange({ ...value, start: clicked, end: clicked });
        setRangeAnchor(clicked);
        return;
      }
      const order = cmpDate(clicked, rangeAnchor);
      if (order >= 0) {
        onChange({ ...value, start: rangeAnchor, end: clicked });
      } else {
        onChange({ ...value, start: clicked, end: rangeAnchor });
      }
      setRangeAnchor(null);
    },
    [multiDay, rangeAnchor, value, onChange],
  );

  const setPreset = useCallback(
    (preset: "today" | "tomorrow" | "nextMon") => {
      const target =
        preset === "today"
          ? todayApi()
          : preset === "tomorrow"
            ? addDaysApi(todayApi(), 1)
            : nextWeekdayApi(1);
      onChange({ ...value, start: target, end: target });
      setMultiDay(false);
      setRangeAnchor(null);
      setViewMonth({ year: target.year, month: target.month, day: 1 });
    },
    [value, onChange],
  );

  const toggleAllDay = useCallback(
    (allDay: boolean) => {
      if (allDay) {
        onChange({ ...value, time: null });
      } else {
        // Restore reasonable defaults when switching from all-day back.
        const now = new Date();
        const startHour = value.time?.start.hour ?? now.getHours();
        const startMin = value.time?.start.minute ?? 0;
        const endHour = value.time?.end.hour ?? (startHour + 1) % 24;
        const endMin = value.time?.end.minute ?? startMin;
        onChange({
          ...value,
          time: {
            start: { hour: startHour, minute: startMin },
            end: { hour: endHour, minute: endMin },
          },
        });
      }
    },
    [value, onChange],
  );

  const toggleMultiDay = useCallback(
    (multi: boolean) => {
      setMultiDay(multi);
      setRangeAnchor(null);
      if (!multi && !isSameDate(value.start, value.end)) {
        // Collapse a current multi-day range to its start.
        onChange({ ...value, end: value.start });
      }
    },
    [value, onChange],
  );

  const triggerLabel = useMemo(() => formatTriggerLabel(value), [value]);

  const popover = open ? (
    <div
      className="dtfield-popover"
      role="dialog"
      aria-label="Date and time picker"
      ref={popoverRef}
      style={
        popoverPos
          ? {
              top: popoverPos.top,
              left: popoverPos.left,
              width: popoverPos.width,
              visibility: "visible",
            }
          : { visibility: "hidden" }
      }
    >
      <MonthGrid
        viewMonth={viewMonth}
        onViewMonthChange={setViewMonth}
        selectionStart={value.start}
        selectionEnd={value.end}
        onSelectDate={handleSelectDate}
      />

      <div className="dtfield-presets">
        <button type="button" className="chip" onClick={() => setPreset("today")}>
          Today
        </button>
        <button type="button" className="chip" onClick={() => setPreset("tomorrow")}>
          Tomorrow
        </button>
        <button type="button" className="chip" onClick={() => setPreset("nextMon")}>
          Next Monday
        </button>
      </div>

      <div className="dtfield-toggles">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={value.time === null}
            onChange={(e) => toggleAllDay(e.target.checked)}
          />
          All day
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={multiDay}
            onChange={(e) => toggleMultiDay(e.target.checked)}
          />
          Multi-day
        </label>
      </div>

      {value.time && (
        <div className="dtfield-times">
          <label className="dtfield-time-cell">
            <span>From</span>
            <input
              type="time"
              step={900}
              value={formatApiTime(value.time.start)}
              onChange={(e) => {
                const next = parseTimeInput(e.target.value);
                if (next && value.time) {
                  onChange({
                    ...value,
                    time: { start: next, end: value.time.end },
                  });
                }
              }}
            />
          </label>
          <label className="dtfield-time-cell">
            <span>To</span>
            <input
              type="time"
              step={900}
              value={formatApiTime(value.time.end)}
              onChange={(e) => {
                const next = parseTimeInput(e.target.value);
                if (next && value.time) {
                  onChange({
                    ...value,
                    time: { start: value.time.start, end: next },
                  });
                }
              }}
            />
          </label>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="dtfield-root">
      <button
        ref={triggerRef}
        type="button"
        className="dtfield-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="dtfield-label">When</span>
        <span className="dtfield-value">{triggerLabel}</span>
        <span className="dtfield-chev" aria-hidden>
          {open ? "▴" : "▾"}
        </span>
      </button>
      {popover && createPortal(popover, document.body)}
    </div>
  );
}
